/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { IconButton, type IconButtonProps, NotificationBadge, type NotificationBadgeProps } from '@coursera/cds-core';

import CoachIcon from 'bundles/ai-coach-platform/components/branding/CoachIcon';
import { isCoachBrandUpdateEnabled } from 'bundles/ai-coach-platform/utils/epicFeatureFlags';

import _t from 'i18n!nls/ai-coach-platform';

const LazySparkleIcon = React.lazy(() => import('bundles/ai-coach-platform/components/building-blocks/SparkleIcon'));

enum IconState {
  DEFAULT = 'default',
  HOVER = 'wink',
  DISABLED = 'disabled',
}

type Props = {
  /**
   * Callback when the launcher CTA is clicked
   */
  onClick?: (selected: boolean) => void;

  /**
   * whether CTA is selected by default
   *
   * @default false
   */
  defaultSelected?: boolean;

  /**
   * whether Coach is disabled on the page
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * size of the Coach icon
   *
   * @default medium
   */
  size?: IconButtonProps['size'];

  tooltip?: string;

  /**
   * Count of notifications to show badge for. Hidden when count is <= 0 or undefined.
   * The max is "9" above which it will render "9+".
   * Implementing team is responsible to update/unset the count based on business logic.
   *
   * @default 0
   */
  notificationCount?: NotificationBadgeProps['value'];

  /**
   * Opt-in to the brand update variant. The variant is enabled when this is true AND the
   * `enableBrandUpdate` EPIC flag is active. Replaces the Coach avatar with a sparkle icon
   * and updates the tooltip and aria-label to "Chat with Coursera AI".
   * @default false
   */
  enableBrandUpdate?: boolean;
};

const styles = {
  iconButton: (isSelected: boolean) => css`
    ${isSelected &&
    `
      .cds-button-label {
        color: var(--cds-color-blue-600);
      }

      background-color: var(--cds-color-emphasis-primary-background-xweak);
      border-radius: var(--cds-border-radius-100);
    `}
  `,
  notificationBadge: css`
    /* Coach theme overrides */
    .cds-notificationBadge-default {
      background: linear-gradient(
        89deg,
        var(--cds-color-emphasis-primary-content-default) -7.38%,
        var(--cds-color-blue-700) 124.79%
      );
    }
  `,
};

export const CoachChatLauncher = (props: Props): React.ReactElement => {
  const {
    onClick,
    disabled = false,
    size = 'medium',
    tooltip,
    defaultSelected = false,
    notificationCount = 0,
    enableBrandUpdate = false,
  } = props;

  const isBrandUpdateEnabled = enableBrandUpdate && isCoachBrandUpdateEnabled();

  const [coachIconState, setCoachIconState] = useState<IconState>(IconState.DEFAULT);
  const [selected, setSelected] = useState(defaultSelected);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const currentIconState = disabled ? IconState.DISABLED : coachIconState;

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  useEffect(() => {
    const handleChatClose = () => {
      // [ACCESS-4084] when chat is closed with Chat close button,
      // set focus back to triggering launcher button
      containerRef.current?.focus();
    };

    if (!disabled) {
      // listen to custom event when Chat close button is clicked
      document.body.addEventListener('coachChatClosed', handleChatClose);
    }

    return () => {
      document.body.removeEventListener('coachChatClosed', handleChatClose);
    };
  }, [disabled]);

  const handleMouseEnter = useCallback<() => void>(() => {
    setCoachIconState(IconState.HOVER);
  }, []);
  const handleFocus = useCallback<() => void>(() => {
    setCoachIconState(IconState.HOVER);
  }, []);

  const handleMouseLeave = useCallback<() => void>(() => {
    setCoachIconState(IconState.DEFAULT);
  }, []);
  const handleBlur = useCallback<() => void>(() => {
    setCoachIconState(IconState.DEFAULT);
  }, []);

  const handleClick = () => {
    setSelected(!selected);
    onClick?.(!selected);
  };

  let tooltipText: string;
  if (isBrandUpdateEnabled) {
    tooltipText = disabled ? _t('Coursera AI Chat is not available on this page.') : _t('Chat with Coursera AI');
  } else {
    tooltipText = disabled
      ? _t('Coursera #{coachLabel} is not available on this page.', { coachLabel: 'Coach' })
      : _t('Chat with Coursera #{coachLabel}', { coachLabel: 'Coach' });
  }

  const ariaLabel = isBrandUpdateEnabled
    ? _t('Chat with Coursera AI')
    : _t('Chat with #{coachLabel}, your AI-powered learning assistant', { coachLabel: 'Coach' });

  return (
    <div ref={containerRef} tabIndex={-1} data-testid="coach-chat-launcher-container">
      <IconButton
        onMouseEnter={disabled ? undefined : handleMouseEnter}
        onMouseLeave={disabled ? undefined : handleMouseLeave}
        onFocus={disabled ? undefined : handleFocus}
        onBlur={disabled ? undefined : handleBlur}
        onClick={disabled ? undefined : handleClick}
        variant="ghost"
        aria-disabled={disabled} // `aria-disabled` allows users to view tooltip while in disabled state
        icon={
          <NotificationBadge value={notificationCount} align="top" css={styles.notificationBadge} max={9}>
            {isBrandUpdateEnabled ? (
              <React.Suspense fallback={null}>
                <LazySparkleIcon size="large" disabled={disabled} isHovered={coachIconState === IconState.HOVER} />
              </React.Suspense>
            ) : (
              <CoachIcon state={currentIconState} size={size} />
            )}
          </NotificationBadge>
        }
        tooltip={tooltip ?? tooltipText}
        aria-label={tooltip ?? ariaLabel}
        aria-expanded={selected}
        data-testid="coach-chat-launcher-button"
        // TODO: once enableBrandUpdate EPIC is cleaned up, remove this attribute and clean up the styles in ExternalCoachLauncherGridItem
        data-brand-update={isBrandUpdateEnabled}
        css={[styles.iconButton(selected)]}
      />
    </div>
  );
};

export default CoachChatLauncher;
